import React, { useState } from 'react';
import { CreateExamDto, ExamType, ResultPublicationMode } from '../../types/exam';

interface ExamFormProps {
  onSubmit: (data: CreateExamDto) => Promise<void>;
  onCancel: () => void;
  initialData?: Partial<CreateExamDto>;
}

export const ExamForm: React.FC<ExamFormProps> = ({ onSubmit, onCancel, initialData }) => {
  const examTypeOptions: Array<{ value: ExamType; label: string }> = [
    { value: 'GENERAL', label: 'General Exam' },
    { value: 'CHAPTER_WISE', label: 'Chapter-wise Exam' },
    { value: 'GRAND_TEST', label: 'Grand Test' },
  ];

  const subjectOptions = [
    'Mathematics',
    'Physics',
    'Chemistry',
    'Biology',
    'English',
    'Hindi',
    'History',
    'Geography',
    'Computer Science',
    'Economics',
    'Political Science',
    'General Science',
  ];

  const standardLevelOptions = [
    'Grade 1',
    'Grade 2',
    'Grade 3',
    'Grade 4',
    'Grade 5',
    'Grade 6',
    'Grade 7',
    'Grade 8',
    'Grade 9',
    'Grade 10',
    'Grade 11',
    'Grade 12',
    'Undergraduate',
    'Postgraduate',
  ];

  const normalizedInitialSubject = (initialData?.subject || '').trim();
  const normalizedInitialStandard = (initialData?.standardLevel || '').trim();

  const resolvedSubjectOptions = normalizedInitialSubject &&
    !subjectOptions.includes(normalizedInitialSubject)
    ? [normalizedInitialSubject, ...subjectOptions]
    : subjectOptions;

  const resolvedStandardLevelOptions = normalizedInitialStandard &&
    !standardLevelOptions.includes(normalizedInitialStandard)
    ? [normalizedInitialStandard, ...standardLevelOptions]
    : standardLevelOptions;

  const [formData, setFormData] = useState<CreateExamDto>({
    title: initialData?.title || '',
    description: initialData?.description || '',
    subject: initialData?.subject || '',
    examType: initialData?.examType || 'GENERAL',
    chapters: initialData?.chapters || [],
    standardLevel: initialData?.standardLevel || '',
    totalMarks: initialData?.totalMarks || 100,
    passingPercentage: initialData?.passingPercentage || 40,
    duration: initialData?.duration || 60,
    negativeMarking: typeof initialData?.negativeMarking === 'number' ? initialData.negativeMarking : 0,
    partialMarking: initialData?.partialMarking || false,
    showAnswersAfterExam: initialData?.showAnswersAfterExam !== false,
    resultPublicationMode: initialData?.resultPublicationMode || 'IMMEDIATE',
    rankGenerationEnabled: initialData?.rankGenerationEnabled !== false,
    certificatesEnabled: initialData?.certificatesEnabled !== false,
    shuffleQuestions: initialData?.shuffleQuestions !== false,
    shuffleOptions: initialData?.shuffleOptions !== false,
    startDate: initialData?.startDate || '',
    endDate: initialData?.endDate || '',
  });
  const [chapterText, setChapterText] = useState((initialData?.chapters || []).join(', '));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? (e.target as HTMLInputElement).checked
          : type === 'number'
          ? parseInt(value, 10)
          : value,
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const chapters = chapterText
      .split(',')
      .map((chapter) => chapter.trim())
      .filter(Boolean);

    // Validation
    if (!formData.title.trim()) {
      setError('Exam title is required');
      return;
    }
    if (!formData.subject.trim()) {
      setError('Subject is required');
      return;
    }
    if (formData.totalMarks <= 0) {
      setError('Total marks must be greater than 0');
      return;
    }
    if (formData.passingPercentage < 0 || formData.passingPercentage > 100) {
      setError('Passing percentage must be between 0 and 100');
      return;
    }
    if (formData.duration < 15 || formData.duration > 480) {
      setError('Duration must be between 15 and 480 minutes');
      return;
    }
    if (!formData.startDate) {
      setError('Start date is required');
      return;
    }
    if (!formData.endDate) {
      setError('End date is required');
      return;
    }
    if (new Date(formData.startDate) >= new Date(formData.endDate)) {
      setError('End date must be after start date');
      return;
    }
    if (new Date(formData.startDate) <= new Date()) {
      setError('Start date must be in future');
      return;
    }
    if (formData.examType === 'CHAPTER_WISE' && chapters.length === 0) {
      setError('Add at least one chapter for chapter-wise exams');
      return;
    }

    try {
      setLoading(true);
      await onSubmit({
        ...formData,
        chapters,
        resultPublicationMode: (formData.resultPublicationMode || 'IMMEDIATE') as ResultPublicationMode,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save exam');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Exam Title *
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter exam title"
          />
        </div>

        {/* Subject */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Subject *
          </label>
          <select
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select subject</option>
            {resolvedSubjectOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        {/* Standard Level */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Standard Level
          </label>
          <select
            name="standardLevel"
            value={formData.standardLevel}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select standard level</option>
            {resolvedStandardLevelOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Exam Type
          </label>
          <select
            name="examType"
            value={formData.examType || 'GENERAL'}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            {examTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Total Marks */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Total Marks *
          </label>
          <input
            type="number"
            name="totalMarks"
            value={formData.totalMarks}
            onChange={handleChange}
            min="1"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Passing Percentage */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Passing Percentage (%) *
          </label>
          <input
            type="number"
            name="passingPercentage"
            value={formData.passingPercentage}
            onChange={handleChange}
            min="0"
            max="100"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Duration */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Duration (minutes) *
          </label>
          <input
            type="number"
            name="duration"
            value={formData.duration}
            onChange={handleChange}
            min="15"
            max="480"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Start Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Start Date *
          </label>
          <input
            type="datetime-local"
            name="startDate"
            value={formData.startDate}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* End Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            End Date *
          </label>
          <input
            type="datetime-local"
            name="endDate"
            value={formData.endDate}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter exam description"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Chapters
        </label>
        <input
          type="text"
          value={chapterText}
          onChange={(event) => setChapterText(event.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          placeholder="Comma-separated chapters, e.g. Algebra, Trigonometry"
        />
        <p className="mt-1 text-xs text-gray-500">
          Used for chapter-wise exams and question bank filtering.
        </p>
      </div>

      {/* Checkboxes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Negative Marking (%)
          </label>
          <select
            name="negativeMarking"
            value={formData.negativeMarking}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value={0}>0% (Disabled)</option>
            <option value={25}>25%</option>
            <option value={33}>33%</option>
            <option value={50}>50%</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Result Publication
          </label>
          <select
            name="resultPublicationMode"
            value={formData.resultPublicationMode || 'IMMEDIATE'}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="IMMEDIATE">Publish immediately after submission</option>
            <option value="MANUAL">Teacher publishes results later</option>
          </select>
        </div>

        <label className="flex items-center">
          <input
            type="checkbox"
            name="partialMarking"
            checked={formData.partialMarking}
            onChange={handleCheckboxChange}
            className="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-700">Enable Partial Marking</span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            name="showAnswersAfterExam"
            checked={formData.showAnswersAfterExam}
            onChange={handleCheckboxChange}
            className="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-700">Show Answers After Exam</span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            name="rankGenerationEnabled"
            checked={formData.rankGenerationEnabled !== false}
            onChange={handleCheckboxChange}
            className="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-700">Generate Ranks</span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            name="certificatesEnabled"
            checked={formData.certificatesEnabled !== false}
            onChange={handleCheckboxChange}
            className="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-700">Auto-generate Certificates</span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            name="shuffleQuestions"
            checked={formData.shuffleQuestions}
            onChange={handleCheckboxChange}
            className="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-700">Shuffle Questions</span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            name="shuffleOptions"
            checked={formData.shuffleOptions}
            onChange={handleCheckboxChange}
            className="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-700">Shuffle Options</span>
        </label>
      </div>

      {/* Buttons */}
      <div className="flex gap-4">
        <button
          type="submit"
          disabled={loading}
          className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-6 py-2 rounded font-medium transition"
        >
          {loading ? 'Saving...' : 'Save Exam'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-300 text-white px-6 py-2 rounded font-medium transition"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default ExamForm;
